import { Dialog, Transition } from '@headlessui/react';
import { useTranslation } from '@refinedev/core';
import Button from 'components/button';
import { IModalProps } from 'components/modal';
import { Fragment, ReactNode, useRef } from 'react';

interface IConfirmModalProps extends IModalProps {
  onConfirm: () => void;
  title: string;
  content: string;
  customContent?: ReactNode;
  hideCancelButton?: boolean;
  customSubmitButton?: ReactNode;
  titleInHeader?: boolean;
  hideActions?: boolean;
}

export const ConfirmModal = ({title, content, customContent, hideCancelButton, closeOnClickBackdrop = true, customSubmitButton, open, onClose, onConfirm, footer, titleInHeader = false, hideActions = false}: IConfirmModalProps) => {
  const { translate } = useTranslation();
  const panelRef = useRef<HTMLDivElement>(null);
  const _submit = () => {
    onConfirm();
    onClose();
  }
  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-[60]" onClose={() => closeOnClickBackdrop ? onClose() : null} initialFocus={hideActions ? panelRef : undefined}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" data-testid="confirm-modal-backdrop" />
        </Transition.Child>

        <div className="fixed inset-0 z-[60] w-screen overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel ref={panelRef} tabIndex={-1} className="relative transform rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 w-full sm:max-w-md outline-none">
                {titleInHeader && <div className="border-b border-gray-200 px-4 py-4 sm:px-6">
                  <Dialog.Title className="text-lg font-semibold leading-6 text-gray-900">{title}</Dialog.Title>
                </div>}
                <div className={titleInHeader ? "px-4 py-5 sm:px-6" : "box-container"}>
                  <div className="text-center">
                    {!titleInHeader && <Dialog.Title as="h1" className="text-lg font-semibold leading-6 text-gray-900">{title}</Dialog.Title>}
                    {customContent ? customContent : (
                      <div className="mt-2">
                        <p className="text-sm text-gray-500 whitespace-pre-line">
                          {content}
                        </p>
                      </div>
                    )}
                  </div>
                  {!hideActions && <div className="flex flex-row items-center gap-x-4 mt-5 sm:mt-6">
                    {footer ?? <>
                    {!hideCancelButton ? (
                      <Button
                        type="button"
                        data-autofocus
                        onClick={onClose}
                        className="inline-flex w-full justify-center bg-white text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 active:bg-gray-100 focus:ring-gray-300"
                      >
                        {translate("button.cancel")}
                      </Button>
                    ) : <></>}
                    {customSubmitButton ? customSubmitButton : (
                      <Button
                        type="button"
                        onClick={_submit}
                        className="inline-flex w-full justify-center sm:col-start-2"
                      >
                        {translate("button.confirm")}
                      </Button>
                    )}
                    </>}
                  </div>}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}
